import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Menu, X, Search } from 'lucide-react';
import { useCart } from '../context/CartContext';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { openCart, itemCount } = useCart();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { href: '/', label: 'Inicio' },
    { href: '/tienda', label: 'Tienda' },
    { href: '/nosotros', label: 'Nosotros' },
    { href: '/contacto', label: 'Contacto' },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/tienda?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-black/80 backdrop-blur-xl border-b border-white/[0.04]'
            : 'bg-black'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-[60px] md:h-[72px]">

            {/* Logo */}
            <Link to="/" className="flex-shrink-0" data-testid="logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_4a150f39-c075-4c53-a862-1e5bef838f84/artifacts/lgkcbt00_image.png"
                alt="D'Avocado Swimwear"
                className="h-8 md:h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation - center */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
              {navLinks.map((link) => {
                const isActive = link.href === '/' ? location.pathname === '/' : location.pathname.startsWith(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`relative text-[13px] tracking-wider font-medium transition-colors duration-200 py-1 ${
                      isActive ? 'text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                    {isActive && (
                      <motion.div
                        layoutId="navIndicator"
                        className="absolute -bottom-0.5 left-0 right-0 h-[1.5px] bg-gold rounded-full"
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1">
              {/* Search */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 ${
                  isSearchOpen ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.04]'
                }`}
                data-testid="search-button"
              >
                {isSearchOpen ? <X className="w-4 h-4" /> : <Search className="w-[18px] h-[18px]" />}
              </button>

              {/* Cart */}
              <button
                onClick={openCart}
                className="relative w-9 h-9 flex items-center justify-center rounded-full text-white/40 hover:text-white/70 hover:bg-white/[0.04] transition-all duration-200"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-[18px] h-[18px]" />
                {itemCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-gold text-black text-[10px] font-bold flex items-center justify-center rounded-full">
                    {itemCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-full text-white/40 hover:text-white/70 hover:bg-white/[0.04] transition-all duration-200"
                data-testid="mobile-menu-button"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar - expands below navbar */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden border-t border-white/[0.04]"
            >
              <form onSubmit={handleSearch} className="max-w-7xl mx-auto px-4 md:px-8 py-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="¿Qué estás buscando?"
                    className="w-full h-10 pl-10 pr-4 bg-white/[0.03] rounded-full text-white text-sm placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-white/10 transition-all"
                    data-testid="search-input"
                    autoFocus
                  />
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-black md:hidden"
          >
            <div className="flex flex-col h-full pt-[72px]">
              {/* Nav links */}
              <div className="flex-1 flex flex-col justify-center px-8 -mt-12">
                {navLinks.map((link, index) => {
                  const isActive = link.href === '/' ? location.pathname === '/' : location.pathname.startsWith(link.href);
                  return (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + index * 0.07, ease: [0.25, 0.1, 0.25, 1] }}
                    >
                      <Link
                        to={link.href}
                        className={`flex items-center justify-between py-5 border-b border-white/[0.03] transition-colors ${
                          isActive ? 'text-white' : 'text-white/40 active:text-white'
                        }`}
                        data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      >
                        <span className="text-[28px] font-serif">{link.label}</span>
                        {isActive && <div className="w-1.5 h-1.5 bg-gold rounded-full" />}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              {/* Bottom */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="px-8 pb-10 space-y-4"
              >
                <div className="flex gap-2.5">
                  <a
                    href="https://instagram.com/davocadoswimwear"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center py-3 bg-white/[0.03] rounded-xl text-white/40 text-sm hover:bg-white/[0.06] transition-colors"
                  >
                    Instagram
                  </a>
                  <a
                    href="https://wa.me/584242925792"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center py-3 bg-[#25D366]/10 rounded-xl text-[#25D366]/70 text-sm hover:bg-[#25D366]/15 transition-colors"
                  >
                    WhatsApp
                  </a>
                </div>
                <p className="text-white/[0.06] text-[10px] text-center tracking-widest uppercase">D'Avocado Swimwear</p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
